import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { CircularLogo, UKFlag } from './HeroSection';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/63xg2u2p_WhatsApp%20Image%202026-03-14%20at%2018.12.05.jpeg";

export const Navigation = ({ language, setLanguage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = {
    en: [
      { label: 'Mission', href: '#mission' },
      { label: 'Crisis', href: '#crisis' },
      { label: 'Technology', href: '#technology' },
      { label: 'Donate', href: '#donate' },
    ],
    es: [
      { label: 'Misión', href: '#mission' },
      { label: 'Crisis', href: '#crisis' },
      { label: 'Tecnología', href: '#technology' },
      { label: 'Donar', href: '#donate' },
    ],
  };

  const scrollToSection = (href) => {
    // For donate links, scroll to donorbox form directly
    const targetSelector = href === '#donate' ? '#donorbox-form' : href;
    const element = document.querySelector(targetSelector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-[#0A1F3A]/90 backdrop-blur-xl py-3' 
            : 'bg-transparent py-5'
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* CIRCULAR Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-3"
            whileHover={{ scale: 1.02 }}
            data-testid="nav-logo"
          >
            <div className={`rounded-full overflow-hidden border-2 border-amber-500/30 transition-all ${
              isScrolled ? 'w-12 h-12' : 'w-14 h-14'
            }`}>
              <img 
                src={LOGO_URL} 
                alt="HYDRO LIFE" 
                className="w-full h-full object-cover"
              />
            </div>
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems[language].map((item, index) => (
              <motion.button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="text-sm font-medium text-white/80 hover:text-amber-300 transition-colors"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`nav-link-${item.href.slice(1)}`}
              >
                {item.label}
              </motion.button>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            {/* UK Flag */}
            <UKFlag className="w-8 h-5 rounded opacity-80" />

            {/* Language Toggle */}
            <div className="flex items-center bg-white/10 rounded-full p-1" data-testid="lang-toggle">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-amber-500 text-[#0A1F3A]' 
                    : 'text-white/70 hover:text-white'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('es')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  language === 'es' 
                    ? 'bg-amber-500 text-[#0A1F3A]' 
                    : 'text-white/70 hover:text-white'
                }`}
              >
                ES
              </button>
            </div>

            {/* CTA - Rose/Coral gradient */}
            <motion.button
              onClick={() => scrollToSection('#donate')}
              className="px-5 py-2 rounded-full bg-gradient-to-r from-rose-500 via-pink-500 to-cyan-500 text-white text-sm font-semibold shadow-lg shadow-rose-500/25"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="nav-donate-btn"
            >
              {language === 'en' ? 'Donate' : 'Donar'}
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden bg-[#0A1F3A]/98 backdrop-blur-xl pt-24"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-6 py-8">
              {/* Circular Logo */}
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-amber-500/30">
                <img src={LOGO_URL} alt="HYDRO LIFE" className="w-full h-full object-cover" />
              </div>

              <div className="flex items-center gap-2">
                <UKFlag className="w-8 h-5" />
                <span className="text-amber-300 text-sm">UK Registered</span>
              </div>

              {navItems[language].map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="text-xl font-medium text-white/90 hover:text-amber-300"
                >
                  {item.label}
                </button>
              ))}
              
              {/* Language Toggle */}
              <div className="flex items-center gap-3 mt-4 bg-white/10 rounded-full px-4 py-2">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-1 rounded-full ${
                    language === 'en' ? 'bg-amber-500 text-[#0A1F3A] font-semibold' : 'text-white/70'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-4 py-1 rounded-full ${
                    language === 'es' ? 'bg-amber-500 text-[#0A1F3A] font-semibold' : 'text-white/70'
                  }`}
                >
                  ES
                </button>
              </div>

              <button
                onClick={() => scrollToSection('#donate')}
                className="mt-4 px-10 py-4 rounded-full bg-gradient-to-r from-rose-500 via-pink-500 to-cyan-500 text-white font-semibold shadow-lg shadow-rose-500/25"
              >
                {language === 'en' ? 'Donate Now' : 'Dona Ahora'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
